import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { PcRecommendedCallout } from '@/components/PcRecommendedCallout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { useSession } from '@/hooks/useSession';
import {
  inputClass,
  labelClass,
  codePreClass,
  settingsPanelClass,
  inlineErrorClass,
  inlineSuccessClass,
} from '@/components/ui/inputStyles';
import { SegmentedTabs } from '@/components/ui/SegmentedTabs';
import { SiteParentInheritBanner, type SiteInheritMeta } from '@/components/SiteParentInheritBanner';
import { cn } from '@/lib/cn';
import { aiProviderLabel, type UserAiProfile } from '@/lib/aiEngine';

const textareaClass = inputClass + ' resize-y';

type IfTab = 'flow' | 'jinshuju' | 'notify' | 'sensitive' | 'ops' | 'platform';

const IF_TABS: { id: IfTab; label: string }[] = [
  { id: 'flow', label: '流程与开关' },
  { id: 'jinshuju', label: '金数据接入' },
  { id: 'notify', label: '自动化' },
  { id: 'sensitive', label: '敏感词管理' },
  { id: 'ops', label: '服务与诊断' },
  { id: 'platform', label: '开放平台地址' },
];

type Resp = {
  ok: boolean;
  site_url: string;
  current_site_id: number;
  current_site_name?: string;
  current_site_code?: string;
  webhook_url: string;
  webhook_header: string;
  component_callback_url: string;
  component_message_url_template?: string;
  auth_redirect_url: string;
  webhook_configured: boolean;
  jinsuju_webhook_secret?: string;
  webhook_ingest_enabled?: boolean;
  push_draft_enabled?: boolean;
  push_manual_review_enabled?: boolean;
  upload_dir?: string;
  upload_dir_realpath?: string;
  upload_dir_exists?: boolean;
  upload_dir_writable?: boolean;
  upload_url_path?: string;
  upload_subpath_hint?: string;
  wecom_review_webhook_url?: string;
  inherit?: SiteInheritMeta;
};

const block = `${settingsPanelClass} font-mono text-xs text-slate-800 break-all`;

type SiteAiBindingResp = {
  ok: boolean;
  site_id: number;
  site_ai_profile_id: number;
  use_default: boolean;
  default_profile_id: number;
  resolved_profile_id: number;
  profiles: UserAiProfile[];
  resolved_label: string;
  ai_provider: string;
  ai_model: string;
  ai_api_key_set: boolean;
};

type WecomWebhookResp = {
  ok: boolean;
  site_id: number;
  wecom_group_webhook_url: string;
  wecom_webhook_msgtype: string;
  wecom_webhook_body_template: string;
};

function buildJinsujuPostUrlWithKey(webhookBase: string, secret: string, siteId?: number): string {
  const sep = webhookBase.includes('?') ? '&' : '?';
  let url = `${webhookBase}${sep}key=${encodeURIComponent(secret)}`;
  if (siteId != null && siteId > 0) {
    url += `&site_id=${siteId}`;
  }
  return url;
}

export function InterfaceInfoPage() {
  const { currentSiteId, capabilities, loading: sessionLoading } = useSession();
  const isSystemAdmin = capabilities.manage_sites;
  const ifTabsVisible = IF_TABS.filter((t) => isSystemAdmin || (t.id !== 'ops' && t.id !== 'platform'));
  const [data, setData] = useState<Resp | null>(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [ingest, setIngest] = useState(true);
  const [pushDraft, setPushDraft] = useState(true);
  const [pushManualReview, setPushManualReview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copyTip, setCopyTip] = useState('');
  const [debugUrl, setDebugUrl] = useState('');
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugOut, setDebugOut] = useState<string | null>(null);
  const [debugErr, setDebugErr] = useState('');

  const [aiProfiles, setAiProfiles] = useState<UserAiProfile[]>([]);
  const [siteAiProfileId, setSiteAiProfileId] = useState(0);
  const [aiDefaultProfileId, setAiDefaultProfileId] = useState(0);
  const [aiResolvedLabel, setAiResolvedLabel] = useState('');
  const [aiResolvedKeySet, setAiResolvedKeySet] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiMsg, setAiMsg] = useState('');
  const [aiErr, setAiErr] = useState('');

  const [wecomUrl, setWecomUrl] = useState('');
  const [wecomMsgtype, setWecomMsgtype] = useState<'text' | 'markdown'>('text');
  const [wecomTpl, setWecomTpl] = useState('');
  const [wecomSaving, setWecomSaving] = useState(false);
  const [wecomMsg, setWecomMsg] = useState('');
  const [wecomErr, setWecomErr] = useState('');
  const [ifTab, setIfTab] = useState<IfTab>('flow');
  const [inheritMeta, setInheritMeta] = useState<SiteInheritMeta | null>(null);

  const [reviewWecomUrl, setReviewWecomUrl] = useState('');
  const [reviewWecomSaving, setReviewWecomSaving] = useState(false);
  const [reviewWecomMsg, setReviewWecomMsg] = useState('');
  const [reviewWecomErr, setReviewWecomErr] = useState('');

  const [sensitiveWords, setSensitiveWords] = useState('');
  const [sensitiveSaving, setSensitiveSaving] = useState(false);
  const [sensitiveLoadErr, setSensitiveLoadErr] = useState('');
  const [sensitiveMsg, setSensitiveMsg] = useState('');
  const [sensitiveErr, setSensitiveErr] = useState('');

  useLayoutEffect(() => {
    const raw = window.location.hash.replace(/^#/, '');
    const legacy: Record<string, IfTab> = {
      'wes-ai-engine': 'notify',
      'wes-wecom-webhook': 'notify',
      'wes-image-fetch-debug': 'ops',
    };
    if (legacy[raw]) {
      setIfTab(legacy[raw]);
      return;
    }
    if (['flow', 'jinshuju', 'notify', 'sensitive', 'ops', 'platform'].includes(raw)) {
      setIfTab(raw as IfTab);
    }
  }, []);

  useEffect(() => {
    if (sessionLoading) {
      return;
    }
    if (!isSystemAdmin && (ifTab === 'ops' || ifTab === 'platform')) {
      setIfTab('flow');
      window.history.replaceState(null, '', '#flow');
    }
  }, [sessionLoading, isSystemAdmin, ifTab]);

  /** 敏感词在「敏感词管理」页签按需加载，避免与主信息接口链式失败时整页出现底栏「加载失败」 */
  const loadSensitiveForTab = useCallback(async () => {
    if (currentSiteId < 1) return;
    setSensitiveLoadErr('');
    try {
      const sr = await apiFetch<{ ok: boolean; sensitive_words?: string }>('get_site_content_settings', {
        method: 'GET',
        query: { site_id: currentSiteId },
      });
      if (sr.ok) {
        setSensitiveWords(sr.sensitive_words ?? '');
      } else {
        setSensitiveLoadErr('无法从服务器读取词表');
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') {
        return;
      }
      setSensitiveLoadErr(e instanceof Error ? e.message : '词表加载失败');
    }
  }, [currentSiteId]);

  useEffect(() => {
    if (ifTab !== 'sensitive' || currentSiteId < 1) {
      return;
    }
    void loadSensitiveForTab();
  }, [ifTab, currentSiteId, loadSensitiveForTab]);

  const load = useCallback(async (options?: { clearSwitchFeedback?: boolean }) => {
    const shouldClearFlowMsg = options?.clearSwitchFeedback !== false;
    setLoading(true);
    if (shouldClearFlowMsg) {
      setMsg('');
      setErr('');
    }
    setAiErr('');
    setWecomErr('');
    try {
      const r = await apiFetch<Resp>('interface_info', {
        method: 'GET',
        query: currentSiteId > 0 ? { site_id: currentSiteId } : undefined,
      });
      if (!r.ok) {
        setErr('主配置（接口与开关）加载失败，请检查网络与登录状态。');
        return;
      }
      setData(r);
      setInheritMeta(r.inherit ?? null);
      setIngest(r.webhook_ingest_enabled !== false);
      setPushDraft(r.push_draft_enabled !== false);
      setPushManualReview(r.push_manual_review_enabled === true);
      setReviewWecomUrl((r.wecom_review_webhook_url ?? '').trim());
      if (currentSiteId > 0) {
        try {
          const ar = await apiFetch<SiteAiBindingResp>('get_site_ai_binding', {
            method: 'GET',
            query: { site_id: currentSiteId },
          });
          if (ar.ok) {
            setAiProfiles(ar.profiles ?? []);
            setSiteAiProfileId(ar.site_ai_profile_id > 0 ? ar.site_ai_profile_id : 0);
            setAiDefaultProfileId(ar.default_profile_id ?? 0);
            setAiResolvedLabel(ar.resolved_label || '');
            setAiResolvedKeySet(ar.ai_api_key_set === true);
          }
          const wr = await apiFetch<WecomWebhookResp>('get_wecom_webhook_settings', {
            method: 'GET',
            query: { site_id: currentSiteId },
          });
          if (wr.ok) {
            setWecomUrl(wr.wecom_group_webhook_url || '');
            setWecomMsgtype(wr.wecom_webhook_msgtype === 'markdown' ? 'markdown' : 'text');
            setWecomTpl(wr.wecom_webhook_body_template || '');
          }
        } catch (e) {
          if (e instanceof Error && e.message === 'SESSION_EXPIRED') {
            return;
          }
          console.error('WES: secondary load (AI / 企微草稿) failed', e);
        }
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') {
        return;
      }
      setErr(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [currentSiteId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveWecomWebhook() {
    if (currentSiteId < 1) return;
    setWecomSaving(true);
    setWecomMsg('');
    setWecomErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_wecom_webhook_settings', {
        method: 'POST',
        body: JSON.stringify({
          site_id: currentSiteId,
          wecom_group_webhook_url: wecomUrl.trim(),
          wecom_webhook_msgtype: wecomMsgtype,
          wecom_webhook_body_template: wecomTpl,
        }),
      });
      if (r.ok) {
        setWecomMsg('已保存企业微信 Webhook 配置');
        await load();
      } else {
        setWecomErr(r.err === 'invalid_wecom_webhook_url' ? 'Webhook 须为 https 地址' : r.err || '保存失败');
      }
    } catch {
      setWecomErr('保存失败');
    } finally {
      setWecomSaving(false);
    }
  }

  async function saveSiteAiBinding() {
    if (currentSiteId < 1) return;
    setAiSaving(true);
    setAiMsg('');
    setAiErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('save_site_ai_binding', {
        method: 'POST',
        body: JSON.stringify({
          site_id: currentSiteId,
          ai_profile_id: siteAiProfileId > 0 ? siteAiProfileId : 0,
          use_default: siteAiProfileId <= 0,
        }),
      });
      if (r.ok) {
        setAiMsg('已保存本站 AI 绑定');
        await load();
      } else {
        setAiErr(r.detail ?? r.err ?? '保存失败');
      }
    } catch {
      setAiErr('保存失败');
    } finally {
      setAiSaving(false);
    }
  }

  async function saveSwitches() {
    if (currentSiteId < 1) return;
    setSaving(true);
    setMsg('');
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean }>('save_interface_switches', {
        method: 'POST',
        body: JSON.stringify({
          site_id: currentSiteId,
          webhook_ingest_enabled: ingest,
          push_draft_enabled: pushDraft,
          push_manual_review_enabled: pushDraft && pushManualReview,
        }),
      });
      if (r.ok) {
        // load() 默认会 setMsg('')，会冲掉成功提示，故在刷新后写入并保留
        await load({ clearSwitchFeedback: false });
        setMsg('已保存数据流开关');
      } else {
        setErr('保存失败，请稍后重试');
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') {
        return;
      }
      setErr(e instanceof Error ? e.message : '保存失败，请检查网络后重试');
    } finally {
      setSaving(false);
    }
  }

  async function saveReviewWecomWebhook() {
    if (currentSiteId < 1) return;
    setReviewWecomSaving(true);
    setReviewWecomMsg('');
    setReviewWecomErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_wecom_review_webhook_settings', {
        method: 'POST',
        body: JSON.stringify({
          site_id: currentSiteId,
          wecom_review_webhook_url: reviewWecomUrl.trim(),
        }),
      });
      if (r.ok) {
        setReviewWecomMsg('已保存待审提醒 Webhook');
        await load();
      } else {
        setReviewWecomErr(
          r.err === 'invalid_wecom_webhook_url' ? 'Webhook 须为 https 地址' : r.err || '保存失败',
        );
      }
    } catch {
      setReviewWecomErr('保存失败');
    } finally {
      setReviewWecomSaving(false);
    }
  }

  async function saveSensitiveTab() {
    if (currentSiteId < 1) return;
    setSensitiveSaving(true);
    setSensitiveMsg('');
    setSensitiveErr('');
    try {
      const r = await apiFetch<{ ok: boolean }>('save_site_sensitive_words', {
        method: 'POST',
        body: JSON.stringify({ site_id: currentSiteId, sensitive_words: sensitiveWords }),
      });
      if (r.ok) {
        setSensitiveMsg('已保存敏感词');
        setSensitiveLoadErr('');
        await load();
      } else {
        setSensitiveErr('保存失败');
      }
    } catch {
      setSensitiveErr('保存失败');
    } finally {
      setSensitiveSaving(false);
    }
  }

  async function runImageFetchDebug() {
    const u = debugUrl.trim();
    if (u === '') {
      setDebugErr('请粘贴金数据图片完整 URL');
      setDebugOut(null);
      return;
    }
    setDebugLoading(true);
    setDebugErr('');
    setDebugOut(null);
    try {
      const r = await apiFetch<{ ok: boolean; debug?: unknown; err?: string }>('debug_jinsuju_image_fetch', {
        method: 'POST',
        body: JSON.stringify({ url: u }),
      });
      if (r.ok && r.debug) {
        setDebugOut(JSON.stringify(r.debug, null, 2));
      } else {
        setDebugErr(r.err || '诊断失败');
      }
    } catch (e) {
      setDebugErr(e instanceof Error ? e.message : '请求失败');
    } finally {
      setDebugLoading(false);
    }
  }

  async function copyToClipboard(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopyTip(`已复制：${label}`);
      window.setTimeout(() => setCopyTip(''), 2500);
    } catch {
      setCopyTip('复制失败，请手动选择文本复制');
      window.setTimeout(() => setCopyTip(''), 3000);
    }
  }

  return (
    <div>
      <PageHeader
        title="接入与推送"
        description={
          isSystemAdmin
            ? '按标签分步配置：数据流开关 → 金数据 Webhook → 自动化（企微 / AI）→ 服务器与排错 → 微信开放平台固定地址。'
            : '按标签配置：数据流开关 → 金数据 Webhook → 自动化（企微 / AI）。'
        }
        actions={
          <Link to="/wechat-official-push">
            <Button type="button" variant="secondary">
              公众号推送
            </Button>
          </Link>
        }
      />

      <PcRecommendedCallout>
        本页含多组可复制 URL、诊断说明与标签切换，建议在电脑浏览器中操作。
      </PcRecommendedCallout>

      {!loading && data && currentSiteId > 0 ? (
        <SiteParentInheritBanner
          scope="interface"
          siteId={currentSiteId}
          inherit={inheritMeta}
          onRestored={() => void load({ clearSwitchFeedback: false })}
        />
      ) : null}

      {loading ? (
        <Spinner label="加载中" />
      ) : err && !data ? (
        <EmptyState>{err}</EmptyState>
      ) : data ? (
        <div className="space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">当前模块</span>
            <SegmentedTabs
              className="min-w-0 flex-1 sm:flex-initial"
              items={ifTabsVisible}
              value={ifTab}
              ariaLabel="接入与推送子模块"
              onChange={(id) => {
                setIfTab(id);
                window.history.replaceState(null, '', `#${id}`);
              }}
            />
          </div>

          {ifTab === 'flow' ? (
          <Card title="数据流开关（当前站点）">
            <p className="mb-4 text-xs text-slate-500">
              流程：金数据提交 → ① 贴图虾入库 → ② 微信第三方写入公众号草稿。可单独关闭①或②，便于联调。
            </p>
            <label className="flex cursor-pointer items-start gap-3 border-b border-slate-100 pb-4">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                checked={ingest}
                onChange={(e) => setIngest(e.target.checked)}
              />
              <span>
                <span className="font-medium text-slate-800">① 接收金数据 Webhook 并入库</span>
                <span className="mt-1 block text-xs text-slate-500">
                  关闭后：校验密钥仍通过，但不写入条目与图片，并返回{' '}
                  <code className="rounded bg-slate-100 px-0.5">ingest_disabled</code>（HTTP 200，便于在金数据侧看到投递结果）。
                </span>
              </span>
            </label>
            <div className="mt-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  checked={pushDraft}
                  onChange={(e) => {
                    const c = e.target.checked;
                    setPushDraft(c);
                    if (!c) setPushManualReview(false);
                  }}
                />
                <span>
                  <span className="font-medium text-slate-800">② 入库后推送到公众号草稿箱</span>
                  <span className="mt-1 block text-xs text-slate-500">
                    关闭后：仍正常入库，但不调用微信草稿接口（可只测金数据解析与本地存图）。需已绑定公众号且①开启时才有草稿。
                  </span>
                </span>
              </label>
              {pushDraft ? (
                <div className="ml-7 mt-3 space-y-3 border-l-2 border-teal-100 pl-3">
                  <p className="rounded-lg border-2 border-amber-300/90 bg-amber-50 px-3 py-2.5 text-sm font-semibold leading-relaxed text-amber-950 shadow-sm">
                    如果命中敏感词，无论是否开启人工审核，都会进入人工审核阶段（不在金数据流中自动写入公众号草稿箱）。
                  </p>
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                      checked={pushManualReview}
                      onChange={(e) => setPushManualReview(e.target.checked)}
                    />
                    <span>
                      <span className="font-medium text-slate-800">开启：人工审核</span>
                      <span className="mt-1 block text-xs text-slate-500">
                        开启时：金数据新入库的每一条都须在后端完成审核，通过后您再在「管理登记」对单条点「推送到公众号草稿箱」。
                        关闭时：若未命中敏感词，则新信息会自动进公众号草稿；若命中敏感词，则仍须先走人工审核，通过后再推草稿。
                        词表在「敏感词管理」或「内容与样式」中维护，两处保存同一数据。
                      </span>
                    </span>
                  </label>
                </div>
              ) : null}
            </div>
            <div className="mt-6 border-t border-slate-100 pt-4">
              <h3 className="text-sm font-semibold text-slate-800">待审提醒：企业微信群 Webhook URL</h3>
              <p className="mt-1 text-xs text-slate-500">
                与下方「自动化」中的「草稿成功」企微群 Webhook 相互独立。仅当金数据入库后，本条在系统中变为
                <strong className="text-slate-800">「待人工审核」</strong>
                （已开启全量人工审核，或标题/正文命中敏感词）时，才会向本地址发送待审提醒，并带审核链接；若列表/详情里仍显示
                <strong className="text-slate-800">「待处理」</strong>
                ，表示尚未进入待审状态，<strong className="text-slate-800">不会</strong>发此通知。请填写 https 的机器人 Webhook，手机/电脑打开链接可登录后进入登记详情完成推送。
              </p>
              <label className={labelClass + ' mt-2'}>
                <span>Webhook URL（可空，空则不发待审通知）</span>
                <input
                  className={inputClass + ' font-mono text-xs'}
                  value={reviewWecomUrl}
                  onChange={(e) => setReviewWecomUrl(e.target.value)}
                  placeholder="https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=..."
                  autoComplete="off"
                />
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => void saveReviewWecomWebhook()}
                  disabled={reviewWecomSaving || currentSiteId < 1}
                >
                  {reviewWecomSaving ? '保存中…' : '保存待审 Webhook'}
                </Button>
              </div>
              {reviewWecomMsg ? <p className={inlineSuccessClass + ' mt-2'}>{reviewWecomMsg}</p> : null}
              {reviewWecomErr ? <p className={inlineErrorClass + ' mt-2'}>{reviewWecomErr}</p> : null}
            </div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <Button onClick={() => void saveSwitches()} disabled={saving || currentSiteId < 1}>
                {saving ? '保存中…' : '保存开关'}
              </Button>
              {msg ? (
                <p className={inlineSuccessClass + ' sm:mt-0'} role="status">
                  {msg}
                </p>
              ) : null}
              {err ? (
                <p className={inlineErrorClass + ' sm:mt-0'} role="alert">
                  {err}
                </p>
              ) : null}
            </div>
          </Card>
          ) : null}

          {ifTab === 'notify' ? (
          <div id="wes-wecom-webhook" className="scroll-mt-24 space-y-6">
            <Card title="企业微信群 Webhook">
              <p className="mb-3 text-xs text-slate-500">
                当<strong className="text-slate-800">公众号草稿写入成功</strong>时（含金数据自动推稿与详情里手动推稿），向企业微信群机器人发送一条通知。请在企业微信后台创建群机器人并复制 Webhook 地址（仅支持{' '}
                <code className="rounded bg-slate-100 px-0.5">https://</code>）。
              </p>
              <label className={labelClass}>
                <span>Webhook URL</span>
                <input
                  className={inputClass + ' font-mono text-xs'}
                  value={wecomUrl}
                  onChange={(e) => setWecomUrl(e.target.value)}
                  placeholder="https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=..."
                  autoComplete="off"
                />
              </label>
              <label className={labelClass}>
                <span>消息类型</span>
                <select
                  className={inputClass}
                  value={wecomMsgtype}
                  onChange={(e) => setWecomMsgtype(e.target.value === 'markdown' ? 'markdown' : 'text')}
                >
                  <option value="text">text（纯文本）</option>
                  <option value="markdown">markdown（支持标题、引用等，见企业微信文档）</option>
                </select>
              </label>
              <label className={labelClass}>
                <span>正文模板（可空，空则使用服务端内置默认）</span>
                <textarea
                  className={textareaClass + ' mt-1 min-h-[160px] font-mono text-xs'}
                  value={wecomTpl}
                  onChange={(e) => setWecomTpl(e.target.value)}
                  placeholder={
                    '占位符：{site_name} {site_code} {title} {entry_id} {media_id} {form_name} {form_code} {serial_number} {draft_mode} {time} {body_excerpt}'
                  }
                  spellCheck={false}
                />
              </label>
              <p className="mb-3 text-[11px] leading-relaxed text-slate-500">
                <code className="rounded bg-slate-100 px-0.5">draft_mode</code> 为「图文」或「贴图」；<code className="rounded bg-slate-100 px-0.5">body_excerpt</code> 为正文前约
                120 字纯文本。单条内容过长时会自动截断以符合企业微信限制。
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => void saveWecomWebhook()} disabled={wecomSaving || currentSiteId < 1}>
                  {wecomSaving ? '保存中…' : '保存 Webhook 配置'}
                </Button>
              </div>
              {wecomMsg ? <p className={inlineSuccessClass + ' mt-2'}>{wecomMsg}</p> : null}
              {wecomErr ? <p className={inlineErrorClass + ' mt-2'}>{wecomErr}</p> : null}
            </Card>

          <div id="wes-ai-engine" className="scroll-mt-24">
            <Card title="本站使用的 AI">
              <p className="mb-4 text-sm text-slate-600">
                大模型 API 在账号下统一维护（
                <Link to="/ai-engines" className="font-medium text-teal-800 underline">
                  AI 接口引擎
                </Link>
                ），用于「内容与样式」中的 <strong className="text-slate-800">AI 写标题</strong>。此处仅选择本站点使用哪一套。
              </p>

              {aiProfiles.length === 0 ? (
                <div className="rounded-xl border border-amber-200/80 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
                  <p className="mb-2">尚未配置任何 AI 接口，AI 写标题将无法调用大模型。</p>
                  <Link to="/ai-engines" className="font-medium text-teal-800 underline underline-offset-2">
                    前往配置 AI 接口引擎 →
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  <label className={labelClass}>
                    <span>本站绑定</span>
                    <select
                      className={inputClass}
                      value={siteAiProfileId}
                      onChange={(e) => setSiteAiProfileId(Number(e.target.value) || 0)}
                    >
                      <option value={0}>
                        使用账号默认
                        {aiDefaultProfileId > 0
                          ? `（${aiProfiles.find((p) => p.id === aiDefaultProfileId)?.label ?? '已设置'}）`
                          : ''}
                      </option>
                      {aiProfiles.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                          {p.is_default ? ' · 账号默认' : ''}
                          {` · ${aiProviderLabel(String(p.ai_provider))}`}
                        </option>
                      ))}
                    </select>
                  </label>
                  {aiResolvedLabel ? (
                    <p className="text-xs text-slate-500">
                      当前生效：<span className="font-medium text-slate-700">{aiResolvedLabel}</span>
                      <span className={cn('ml-2', aiResolvedKeySet ? 'text-emerald-700' : 'text-amber-700')}>
                        {aiResolvedKeySet ? 'Key 已配置' : 'Key 未配置'}
                      </span>
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={() => void saveSiteAiBinding()}
                      disabled={aiSaving || currentSiteId < 1}
                    >
                      {aiSaving ? '保存中…' : '保存本站绑定'}
                    </Button>
                    <Link
                      to="/ai-engines"
                      className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      管理 AI 配置
                    </Link>
                  </div>
                </div>
              )}
              {aiMsg ? <p className={cn(inlineSuccessClass, 'mt-2')}>{aiMsg}</p> : null}
              {aiErr ? <p className={cn(inlineErrorClass, 'mt-2')}>{aiErr}</p> : null}
            </Card>
          </div>
          </div>
          ) : null}

          {ifTab === 'sensitive' ? (
            <Card title="敏感词管理（当前站点）">
              <p className="mb-3 text-xs text-slate-500">
                一行一词；标题与正文中只要包含（忽略大小写）即视为命中。命中时金数据自动流不会直推微信草稿，须人工审核后再从「管理登记」推送。与「流程与开关」中「开启：人工审核」同时存在时，敏感词优先：只要命中敏感词，即进入人工审核阶段。词表与「内容与样式」页内「内容安全：敏感词」为同一配置，任一处保存即可。
              </p>
              {sensitiveLoadErr ? <p className={inlineErrorClass + ' mb-3'}>{sensitiveLoadErr}</p> : null}
              <label className={labelClass}>
                <span>词表</span>
                <textarea
                  className={textareaClass + ' min-h-[160px] font-mono text-xs'}
                  value={sensitiveWords}
                  onChange={(e) => setSensitiveWords(e.target.value)}
                  placeholder={'每行一个关键词或短语，例如：\n微信\n测试词'}
                  spellCheck={false}
                />
              </label>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => void saveSensitiveTab()}
                  disabled={sensitiveSaving || currentSiteId < 1}
                >
                  {sensitiveSaving ? '保存中…' : '保存敏感词'}
                </Button>
                <Link
                  to="/settings"
                  className="inline-flex items-center justify-center rounded-xl border border-slate-200/90 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50/90"
                >
                  打开「内容与样式」
                </Link>
              </div>
              {sensitiveMsg ? <p className={inlineSuccessClass + ' mt-2'}>{sensitiveMsg}</p> : null}
              {sensitiveErr ? <p className={inlineErrorClass + ' mt-2'}>{sensitiveErr}</p> : null}
            </Card>
          ) : null}

          {ifTab === 'ops' ? (
          <>
          {data.upload_dir ? (
            <Card title="本地上传目录（金数据图片落盘）">
              <p className="mb-3 text-xs text-slate-500">
                配置在 <code className="rounded bg-slate-100 px-1">api/config.php</code> 的{' '}
                <code className="rounded bg-slate-100 px-1">upload_dir</code>。若站点从{' '}
                <code className="rounded bg-slate-100 px-1">dist/</code> 部署，通常为{' '}
                <strong className="text-slate-800">dist/uploads</strong>（与项目根下的 <code className="rounded bg-slate-100 px-1">uploads</code>{' '}
                不是同一目录）。子路径：<code className="rounded bg-slate-100 px-1">{data.upload_subpath_hint ?? 'wes/年/月/日/'}</code>
              </p>
              <div className={block}>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">配置路径</div>
                {data.upload_dir_realpath || data.upload_dir}
              </div>
              <p className="mt-3 text-xs text-slate-600">
                目录存在：{data.upload_dir_exists ? '是' : '否'}
                {' · '}
                可写：{data.upload_dir_writable ? '是' : '否'}
                {data.upload_url_path ? (
                  <>
                    {' · '}
                    URL 前缀：<code className="rounded bg-slate-100 px-1">{data.upload_url_path}</code>
                  </>
                ) : null}
              </p>
              {!data.upload_dir_exists ? (
                <p className="mt-2 text-xs text-amber-800">
                  目录尚不存在时，首次成功从金数据下载图片后会自动创建；若长期无文件，多为远程图下载失败（见历史说明），或 Web 服务器未把{' '}
                  <code className="rounded bg-amber-100 px-1">{data.upload_url_path || '/uploads'}</code>{' '}
                  映射到上述磁盘路径。
                </p>
              ) : null}
            </Card>
          ) : null}

          <div id="wes-image-fetch-debug" className="scroll-mt-24">
          <Card title="金数据图片拉取诊断（排错）">
            <p className="mb-3 text-xs text-slate-500">
              <strong className="text-slate-700">与公众号的关系</strong>：金数据只提供图片下载链接；贴图虾需要先把图
              <strong className="text-slate-800">下载到本服务器</strong>（<code className="rounded bg-slate-100 px-1">upload_dir</code>
              ），再调用微信「上传图文/贴图素材」接口得到 <code className="rounded bg-slate-100 px-1">media_id</code>，最后写入草稿。
              若详情里显示「未落盘」，说明第一步失败，草稿也无法带图。
            </p>
            <p className="mb-3 text-xs text-slate-500">
              将金数据返回的 <code className="rounded bg-slate-100 px-1">gd-pri.jinshujufiles.com</code> 完整链接粘贴到下方，点击运行诊断。返回 JSON 中{' '}
              <code className="rounded bg-slate-100 px-1">success</code> 为 true 表示当前服务器可拉取图片；否则查看各{' '}
              <code className="rounded bg-slate-100 px-1">attempts</code> 里的 <code className="rounded bg-slate-100 px-1">http_code</code>、
              <code className="rounded bg-slate-100 px-1">curl_error</code>、<code className="rounded bg-slate-100 px-1">body_preview</code>（常见为 403/HTML）。
              服务端失败时还会写一行到 PHP <code className="rounded bg-slate-100 px-1">error_log</code>（关键词 <code className="rounded bg-slate-100 px-1">WES image_fetch failed</code>）。
            </p>
            <label className={labelClass + ' mb-2 block'}>
              <span>图片 URL</span>
              <textarea
                className={block + ' mt-1 min-h-[72px] resize-y'}
                placeholder="https://gd-pri.jinshujufiles.com/..."
                value={debugUrl}
                onChange={(e) => setDebugUrl(e.target.value)}
                spellCheck={false}
              />
            </label>
            <Button type="button" onClick={() => void runImageFetchDebug()} disabled={debugLoading}>
              {debugLoading ? '诊断中…' : '运行诊断'}
            </Button>
            {debugErr ? <p className={inlineErrorClass + ' mt-2'}>{debugErr}</p> : null}
            {debugOut ? (
              <pre className={codePreClass + ' mt-3'}>
                {debugOut}
              </pre>
            ) : null}
          </Card>
          </div>

          </>
          ) : null}

          {ifTab === 'jinshuju' ? (
          <Card title="金数据 Webhook">
            <div className="mb-3 space-y-2 text-sm text-slate-600">
              <p className="text-xs text-slate-500">
                当前工作站点：
                <span className="font-medium text-slate-800">
                  {data.current_site_name || '—'}
                  {data.current_site_code ? (
                    <span className="text-slate-500">（{data.current_site_code}，ID {data.current_site_id}）</span>
                  ) : (
                    <span className="text-slate-500">（ID {data.current_site_id}）</span>
                  )}
                </span>
              </p>
            </div>

            {data.webhook_url === '' ? (
              <p className="text-sm text-amber-800">请先在 config.php 配置 site_url。</p>
            ) : !data.webhook_configured || !(data.jinsuju_webhook_secret && data.jinsuju_webhook_secret.length > 0) ? (
              <div className={inlineErrorClass}>
                <span className="font-medium">当前站点尚未配置密钥，</span>
                无法生成带 <code className="mx-0.5 rounded bg-white/80 px-1 text-xs">key=</code> 的完整地址。
                <Link to="/sites" className="ml-1 font-semibold text-rose-950 underline underline-offset-2">
                  前往站点管理 → 修改密钥
                </Link>
              </div>
            ) : (
              <>
                <p className="mb-1 text-xs font-medium text-slate-700">
                  推荐：将下方「完整 POST 地址」整段复制到金数据的 POST 地址框（已含当前站点密钥与 site_id；二级站与一级站共用密钥时请勿去掉 site_id 参数）
                </p>
                {inheritMeta?.parent_site_id ? (
                  <p className="mb-2 text-xs text-sky-900/90">
                    本二级站可与一级站共用 Webhook 密钥；请务必使用下方带站点 ID 的地址，以免入库到错误站点。
                  </p>
                ) : null}
                <div className="flex flex-wrap items-start gap-2">
                  <div className={`${block} min-w-0 flex-1`}>
                    {buildJinsujuPostUrlWithKey(data.webhook_url, data.jinsuju_webhook_secret ?? '', data.current_site_id)}
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="shrink-0"
                    onClick={() =>
                      void copyToClipboard(
                        buildJinsujuPostUrlWithKey(data.webhook_url, data.jinsuju_webhook_secret ?? '', data.current_site_id),
                        '完整 POST 地址'
                      )
                    }
                  >
                    复制地址
                  </Button>
                </div>
                <p className="mb-1 mt-4 text-xs font-medium text-slate-700">
                  可选：若环境支持自定义请求头，可用下方基础地址 + 请求头（与上一段密钥相同）
                </p>
                <div className={block}>{data.webhook_url}</div>
                <div className="mt-2 flex flex-wrap items-start gap-2">
                  <div className="min-w-0 flex-1 rounded-xl border border-slate-200/90 bg-white/95 p-2.5 font-mono text-xs text-slate-800 shadow-sm break-all">
                    X-Wes-Webhook-Secret: {data.jinsuju_webhook_secret}
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="shrink-0"
                    onClick={() =>
                      void copyToClipboard(
                        `X-Wes-Webhook-Secret: ${data.jinsuju_webhook_secret ?? ''}`,
                        '请求头一行'
                      )
                    }
                  >
                    复制请求头
                  </Button>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  密钥出现在 URL 或日志中时请自行评估风险。若金数据无法保存带参数的地址，请咨询金数据套餐是否支持 URL 传参。
                </p>
              </>
            )}
            {copyTip ? <p className={inlineSuccessClass + ' mt-2'}>{copyTip}</p> : null}
          </Card>
          ) : null}

          {ifTab === 'platform' ? (
          <>
          <Card title="第三方平台 — 消息与事件接收（须含 $APPID$）">
            <p className="mb-2 text-xs text-slate-500">
              与「授权事件」不同：此处<strong className="text-slate-800">必须</strong>带占位符{' '}
              <code className="rounded bg-slate-100 px-0.5">/$APPID$</code>，与参考项目{' '}
              <code className="rounded bg-slate-100 px-0.5">wx_tp_message.php/$APPID$</code> 同类。整段复制到开放平台对应输入框（勿填
              auth_redirect）。
            </p>
            <div className={block}>{data.component_message_url_template || '—'}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() =>
                  void copyToClipboard(data.component_message_url_template || '', '消息与事件接收 URL')
                }
              >
                复制
              </Button>
            </div>
          </Card>

          <Card title="第三方平台 — 授权事件接收（票据 / ticket）">
            <p className="mb-2 text-xs text-slate-500">开放平台「开发资料」- 授权事件接收 URL（无 $APPID$）</p>
            <div className={block}>{data.component_callback_url || '—'}</div>
          </Card>

          <Card title="授权回调（扫码后）">
            <p className="mb-2 text-xs text-slate-500">开放平台「开发资料」- 授权完成回调域名指向本站域名，路径如下</p>
            <div className={block}>{data.auth_redirect_url || '—'}</div>
          </Card>

          <Card title="站点与 URL">
            <p className="text-sm text-slate-600">
              site_url（config）：<span className="font-mono">{data.site_url || '—'}</span>
            </p>
            <p className="mt-1 text-sm text-slate-600">当前管理端站点 ID：{data.current_site_id}</p>
          </Card>
          </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
